<script setup lang="ts">
import { cn } from '@/utils';

import { creditSimulatorFieldVariants } from '.';
import CreditSimulatorMoneyInput from './CreditSimulatorMoneyInput.vue';

export interface CreditSimulatorAmountFieldProps {
	label: string;
	rangeId: string;
	textId: string;
	textLabel: string;
	/** Typed value for plain fields (with a unit) */
	textValue?: string;
	/** Amount for money fields (without a unit) */
	amountValue?: number;
	rangeValue: number;
	min: number;
	max: number;
	step: number;
	unit?: string;
}

const props = defineProps<CreditSimulatorAmountFieldProps>();

const emit = defineEmits<{
	type: [text: string];
	amount: [value: number];
	commit: [];
	slide: [value: number];
}>();

function handleType(event: Event) {
	emit('type', (event.target as HTMLInputElement).value);
}

// Rounded so a percentage-based amount does not reformat the field on every keystroke.
function toMoneyValue(value: number | undefined): number | null {
	return value && value > 0 ? Math.round(value) : null;
}

function handleSlide(event: Event) {
	emit('slide', Number((event.target as HTMLInputElement).value));
}
</script>

<template>
	<div>
		<div class="flex flex-wrap items-center justify-between gap-3">
			<label :for="rangeId" class="text-[14px] text-[#A3A3A3]">
				{{ label }}
			</label>
			<span v-if="unit" class="inline-flex items-center gap-2">
				<input
					:id="textId"
					type="text"
					inputmode="numeric"
					:aria-label="textLabel"
					:value="textValue"
					:class="
						cn(creditSimulatorFieldVariants({ variant: 'value' }), 'w-[68px]')
					"
					@input="handleType"
					@blur="emit('commit')"
				/>
				<span class="text-[15px] font-medium text-[#D4D4D4]">{{ unit }}</span>
			</span>
			<CreditSimulatorMoneyInput
				v-else
				:id="textId"
				:label="textLabel"
				:model-value="toMoneyValue(amountValue)"
				@update:model-value="emit('amount', $event ?? 0)"
				@commit="emit('commit')"
			/>
		</div>
		<input
			:id="rangeId"
			type="range"
			:min="props.min"
			:max="props.max"
			:step="props.step"
			:value="rangeValue"
			class="m-0 h-11 w-full accent-beige"
			@input="handleSlide"
		/>
	</div>
</template>
